/*
 * lunna.js — Lunna Pet Co. behaviour layer for Shopify Origin 15.5.0
 *
 * Four small jobs, none of which duplicate Shopify behaviour:
 *   1. Repair the `form` attribute on the sticky bar and final CTA buttons.
 *   2. Show the mobile sticky Add to cart once the purchase area scrolls away.
 *   3. Re-index the kit builder's line-item names before it posts to /cart/add.
 *   4. Flash the peach tint on the line that was just added (§9.4).
 *
 * The cart API, the product form, variant selection, the drawer and every error
 * state stay Origin's own. Nothing here intercepts a submit or calls /cart.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------
   * The one product form on the page. The sticky bar and the final CTA both
   * point at it by id; if the main product section was renamed in the editor
   * that id will not resolve, so fall back to whatever form is actually there.
   * ------------------------------------------------------------------- */
  function mainProductForm() {
    var host = document.querySelector('product-form form[action*="/cart/add"]');
    return host || document.querySelector('form[action*="/cart/add"][id^="product-form-"]');
  }

  function repairFormAttributes() {
    var buttons = document.querySelectorAll('[data-lunna-product-form-button]');
    if (!buttons.length) return;

    var form = mainProductForm();
    // Read the attribute, not `form.id`: the form owns an `<input name="id">`
    // for the variant, and named controls shadow the property, so `form.id`
    // returns that input element rather than the form's id string.
    var formId = form && form.getAttribute('id');
    if (!formId) return;

    buttons.forEach(function (button) {
      if (button.getAttribute('form') !== formId) {
        button.setAttribute('form', formId);
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Mobile sticky Add to cart. Visibility is driven by an IntersectionObserver
   * on the main form, exactly as specified — the bar submits that same form.
   * ------------------------------------------------------------------- */
  if (!customElements.get('lunna-sticky-atc')) {
    customElements.define(
      'lunna-sticky-atc',
      class LunnaStickyAtc extends HTMLElement {
        connectedCallback() {
          this.form = mainProductForm();
          if (!this.form) return;

          /* One rule decides this: the bar belongs on screen only once the buy
             area is entirely behind the visitor.

             This was an IntersectionObserver, which is the wrong instrument for
             it. An observer reports threshold *crossings*, and the form can go
             from below the fold to above the viewport without ever crossing —
             a restored scroll position on a back navigation does exactly that.
             No crossing meant no callback, and the bar stayed hidden for the
             rest of the visit. Reading the geometry on a passive, rAF-coalesced
             scroll is both cheaper to reason about and correct from any
             starting position. */
          this.evaluate();

          this.scrollHandler = function () {
            if (this.scrollQueued) return;
            this.scrollQueued = true;
            window.requestAnimationFrame(
              function () {
                this.scrollQueued = false;
                this.evaluate();
              }.bind(this)
            );
          }.bind(this);

          window.addEventListener('scroll', this.scrollHandler, { passive: true });
          window.addEventListener('resize', this.scrollHandler, { passive: true });

          /* The bar is its own section, so Origin's variant re-render never
             reaches it: the price, the label and the disabled state would keep
             describing whichever variant happened to render first. Re-render
             this section for the chosen variant instead of formatting money
             here — the Liquid already knows the shop's format and the sold-out
             wording, and duplicating either in JS is how the two drift apart. */
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            this.unsubscribe = subscribe(
              PUB_SUB_EVENTS.variantChange,
              function (event) {
                var variant = event && event.data && event.data.variant;
                if (variant) this.refresh(variant.id);
              }.bind(this)
            );
          }
        }

        /* Swaps only the row inside the bar, so the element, its observer and
           the subscription above all survive the update. */
        refresh(variantId) {
          var sectionId = this.getAttribute('data-section-id');
          if (!sectionId || !variantId) return;

          var url =
            window.location.pathname +
            '?section_id=' +
            encodeURIComponent(sectionId) +
            '&variant=' +
            encodeURIComponent(variantId);

          fetch(url)
            .then(function (response) {
              return response.ok ? response.text() : Promise.reject();
            })
            .then(
              function (markup) {
                var next = new DOMParser()
                  .parseFromString(markup, 'text/html')
                  .querySelector('.lunna-sticky-atc__inner');
                var current = this.querySelector('.lunna-sticky-atc__inner');
                if (!next || !current) return;

                current.replaceWith(next);
                /* The replacement button arrives with the section's own guess
                   at the form id, which is the guess repairFormAttributes
                   exists to correct. */
                repairFormAttributes();
              }.bind(this)
            )
            .catch(function () {
              /* Leave the previous row in place: stale beats empty. */
            });
        }

        evaluate() {
          if (!this.form) return;
          /* The form's bottom edge is at or above zero only when the whole
             buy area has scrolled off the top. While it is in view, and while
             it is still further down the page, that edge is positive — so one
             comparison separates "passed it" from both other positions. */
          var passed = this.form.getBoundingClientRect().bottom <= 0;
          this.toggleAttribute('hidden', !passed);
        }

        disconnectedCallback() {
          if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
            window.removeEventListener('resize', this.scrollHandler);
          }
          if (this.unsubscribe) this.unsubscribe();
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
   * Kit builder. Unchecked boxes submit nothing, which leaves gaps in the
   * `items[][id]` sequence. Re-index to `items[N][id]` immediately before the
   * POST so the cart API always receives a dense array.
   * ------------------------------------------------------------------- */
  function wireKitForms() {
    document.querySelectorAll('[data-lunna-kit-form]').forEach(function (form) {
      if (form.dataset.lunnaKitWired === 'true') return;
      form.dataset.lunnaKitWired = 'true';

      form.addEventListener('submit', function () {
        var index = 0;
        form.querySelectorAll('input[name$="[id]"]').forEach(function (input) {
          if (input.type === 'checkbox' && !input.checked) return;
          if (input.disabled) return;
          input.name = 'items[' + index + '][id]';
          index += 1;
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Peach add-to-cart feedback (§9.4). Origin publishes `cart-update` with the
   * variant that was added; find that line in the drawer and tint it for 400ms.
   * Under reduced motion the tint is held rather than faded, per the spec.
   * ------------------------------------------------------------------- */
  function flashAddedLine(variantId) {
    if (!variantId) return;

    window.requestAnimationFrame(function () {
      var marker = document.querySelector('cart-drawer [data-variant-id="' + variantId + '"]');
      if (!marker) return;

      var line = marker.closest('.cart-item');
      if (!line) return;

      line.classList.remove('lunna-just-added');
      // Force a reflow so the animation restarts when the same line is re-added.
      void line.offsetWidth;
      line.classList.add('lunna-just-added');

      if (reduceMotion.matches) return;
      window.setTimeout(function () {
        line.classList.remove('lunna-just-added');
      }, 500);
    });
  }

  var cartSubscribed = false;

  function subscribeToCart() {
    if (cartSubscribed) return;
    if (typeof window.subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    cartSubscribed = true;

    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      flashAddedLine(event.productVariantId);
    });
  }

  function init() {
    repairFormAttributes();
    wireKitForms();
    subscribeToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
